import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spinner, Text } from "@marekl/adminer/components";
import { formatDuration } from "@marekl/adminer/format";
import { Pause, Play } from "lucide-react";
import styles from "./MusicPlayer.module.css";

// MusicPlayer plays one stored recording, draws its waveform, and draws
// the rules on top of it.
//
// It exists so a leader can answer "is this the right track?" before the
// day of the event, which is the only moment the answer is cheap — and
// the waveform answers it at a glance, without listening: the shape of a
// routine is recognisable, and a file that is silence, or half the length
// it should be, shows it before you press play.
//
// It draws the recording and nothing else. A line marking the time
// limit used to sit on top of it; it was read as a fault in the file
// rather than as a rule, so the limit is now said in words only — the
// „Delší/Kratší než limit" warning under the player.
//
// Everything here is the platform: fetch the bytes once,
// AudioContext.decodeAudioData for the peaks, a canvas to draw them, and
// a blob URL for an <audio> element to do the actual playback. No
// waveform library, and no second download — the same ArrayBuffer serves
// both jobs.
//
// NOTE: a candidate to promote to `@marekl/adminer` once an upstream
// media primitive exists. Keep it free of cmfis domain types.

/** Peaks are measured at this resolution and downsampled to whatever
 * number of bars actually fits, so the detail survives a wide layout and
 * a resize without decoding again. */
const PEAK_RESOLUTION = 1024;
/** Slot width in CSS pixels: a bar plus its gap. */
const SLOT_PX = 3;
/** Arrow-key step. Five seconds finds a passage; Shift drops to one for
 * the moment somebody is checking exactly where the music starts. */
const SEEK_STEP_SECONDS = 5;

type LoadState = "idle" | "loading" | "ready" | "failed";

export function MusicPlayer({
  src,
  className,
}: {
  src: string;
  className?: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [state, setState] = useState<LoadState>("idle");
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  // One fetch feeds both the player and the waveform.
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setState("loading");
    setPeaks(null);
    setPlaying(false);
    setPosition(0);

    (async () => {
      try {
        const res = await fetch(src, { credentials: "include" });
        if (!res.ok) throw new Error(String(res.status));
        const buffer = await res.arrayBuffer();
        if (cancelled) return;

        url = URL.createObjectURL(new Blob([buffer], { type: "audio/mpeg" }));
        setObjectUrl(url);
        setState("ready");

        // Decoding detaches the ArrayBuffer, so hand it a copy — the
        // blob above still needs the original.
        const ctx = new AudioContext();
        try {
          const decoded = await ctx.decodeAudioData(buffer.slice(0));
          if (!cancelled) {
            setPeaks(computePeaks(decoded));
            setDuration(decoded.duration);
          }
        } finally {
          void ctx.close();
        }
      } catch {
        // Playback is the point; the waveform is a bonus. A decode
        // failure leaves the controls working with a flat bar.
        if (!cancelled) setState((s) => (s === "ready" ? "ready" : "failed"));
      }
    })();

    // Captured now, not read in the cleanup: by then the ref may already
    // point somewhere else (the lint rule exists for exactly that).
    const audio = audioRef.current;
    return () => {
      cancelled = true;
      // Pause before letting go. React detaching the element does NOT
      // stop playback — close the dialog mid-track and the music keeps
      // going with no control left to stop it. Replacing a recording
      // remounts on a new key, so without this the two tracks overlap.
      audio?.pause();
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (width === 0 || height === 0) return;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, width, height);

    const style = getComputedStyle(canvas);
    const token = (name: string, fallback: string) =>
      style.getPropertyValue(name).trim() || fallback;
    const played = token("--wave-played", "#4f46e5");
    const pending = token("--wave-pending", "#cbd5e1");

    // Only as many bars as fit: at three pixels a slot the shape stays
    // detailed on a wide layout and never collapses into hairlines.
    const barCount = Math.max(24, Math.min(PEAK_RESOLUTION, Math.floor(width / SLOT_PX)));
    const bars = resample(peaks, barCount);
    const slot = width / barCount;
    const barWidth = Math.max(1.5, slot - 1);
    const radius = Math.min(barWidth / 2, 1.5);

    const progress = duration > 0 ? position / duration : 0;

    bars.forEach((peak, i) => {
      const x = i * slot;
      const h = Math.max(2, peak * (height - 2));
      ctx.fillStyle = i / barCount < progress ? played : pending;
      ctx.beginPath();
      ctx.roundRect(x, (height - h) / 2, Math.min(barWidth, slot), h, radius);
      ctx.fill();
    });
  }, [peaks, position, duration]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    const onResize = () => draw();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [draw]);

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play();
    else audio.pause();
  }

  // Clicking the waveform seeks, which is the whole reason it is drawn
  // rather than printed.
  function seekTo(seconds: number) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const at = Math.min(duration, Math.max(0, seconds));
    audio.currentTime = at;
    setPosition(at);
  }

  function seekFromPointer(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas || duration <= 0) return;
    const rect = canvas.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    seekTo(fraction * duration);
  }

  // The waveform is the only way to scrub, so it has to answer the
  // keyboard too — as a slider, which is what it already behaves like.
  function seekFromKey(e: React.KeyboardEvent<HTMLCanvasElement>) {
    const step = e.shiftKey ? 1 : SEEK_STEP_SECONDS;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowUp":
        seekTo(position + step);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        seekTo(position - step);
        break;
      case "Home":
        seekTo(0);
        break;
      case "End":
        seekTo(duration);
        break;
      case " ":
      case "Enter":
        toggle();
        break;
      default:
        return;
    }
    // Only once a key was actually handled: otherwise Tab and the
    // browser's own shortcuts stop working inside the player.
    e.preventDefault();
  }

  return (
    <div className={className ? `${styles.player} ${className}` : styles.player}>
      <Button
        className={styles.toggle}
        onClick={toggle}
        disabled={state !== "ready"}
        aria-label={playing ? "Pozastavit" : "Přehrát"}
        startIcon={playing ? <Pause size={16} /> : <Play size={16} />}
      />
      <div className={styles.wave}>
        {state === "loading" && !peaks && (
          <div className={styles.overlay}>
            <Spinner />
          </div>
        )}
        {/* A slider rather than a decoration: it is the ONLY way to
            scrub, so role="presentation" put the sole affordance for
            that behaviour outside the accessibility tree. The value is
            announced in minutes and seconds, not in raw seconds, because
            "sto padesát sedm" is not a position in a routine. */}
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          onClick={seekFromPointer}
          onKeyDown={seekFromKey}
          tabIndex={state === "ready" ? 0 : -1}
          role="slider"
          aria-label="Pozice v nahrávce"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(position)}
          aria-valuetext={`${formatClock(position)} z ${formatClock(duration)}`}
        />
      </div>
      <Text variant="mono" className={styles.time}>
        {formatClock(position)} / {formatClock(duration)}
      </Text>
      {objectUrl && (
        // Hidden: the controls above are the interface. The element is
        // still what actually plays, so keyboard media keys work.
        <audio
          ref={audioRef}
          src={objectUrl}
          className={styles.audio}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setPosition(0);
          }}
          onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            // Fallback for a file the decoder could not read: the
            // element still knows how long it is.
            //
            // Read `duration` HERE, not inside the updater: React clears
            // the synthetic event's currentTarget once the handler
            // returns, and the updater runs after that — reaching for it
            // there threw "Cannot read properties of null".
            const seconds = e.currentTarget.duration;
            if (Number.isFinite(seconds)) {
              setDuration((d) => d || seconds);
            }
          }}
        >
          {/* No <track>: a competition backing track has no dialogue to caption. */}
        </audio>
      )}
    </div>
  );
}

/** computePeaks reduces the decoded audio to PEAK_RESOLUTION normalised
 * peaks — more than any layout draws, so resample() always has detail to
 * spend. */
function computePeaks(buffer: AudioBuffer): number[] {
  const channel = buffer.getChannelData(0);
  const step = Math.max(1, Math.floor(channel.length / PEAK_RESOLUTION));
  const peaks: number[] = [];
  let loudest = 0;
  for (let i = 0; i < PEAK_RESOLUTION; i++) {
    let peak = 0;
    const start = i * step;
    // Sampling rather than scanning every frame: a 4-minute track is ten
    // million samples, and the shape is identical either way.
    for (let j = start; j < start + step && j < channel.length; j += 8) {
      const v = Math.abs(channel[j]);
      if (v > peak) peak = v;
    }
    peaks.push(peak);
    if (peak > loudest) loudest = peak;
  }
  // Normalise so a quietly mastered track still fills the box; the
  // waveform answers "what shape is this?", not "how loud is it?".
  const scale = loudest > 0 ? 1 / loudest : 1;
  return peaks.map((p) => Math.min(1, p * scale));
}

/** resample takes the loudest peak of each output bucket, so a transient
 * survives being drawn at a tenth of the resolution it was measured at. */
function resample(peaks: number[] | null, count: number): number[] {
  if (!peaks || peaks.length === 0) return new Array<number>(count).fill(0.06);
  if (peaks.length === count) return peaks;
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const from = Math.floor((i * peaks.length) / count);
    const to = Math.max(from + 1, Math.floor(((i + 1) * peaks.length) / count));
    let peak = 0;
    for (let j = from; j < to && j < peaks.length; j++) {
      if (peaks[j] > peak) peak = peaks[j];
    }
    out.push(peak);
  }
  return out;
}

// The guard is the whole reason this is not the bare import: a player reads
// `currentTime` off an <audio> that may not have loaded yet, and „0:00" is
// what an unstarted track shows — not the „—" placeholder the formatter
// reserves for a value nobody has.
function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  return formatDuration(seconds, { format: "ms" });
}
